import axios from "axios";
import {
  call,
  put,
  takeEvery,
  takeLatest,
  select,
  take,
} from "redux-saga/effects";
import { eventChannel, END } from "redux-saga";
import constants from "constants";
import actions from "actions";
import { toast } from "react-toastify";
import { push } from "redux-router";
import _ from "lodash";
import validator from "../services/validator";
import jsFileDownload from "js-file-download";
import {
  getAdminSystemFeedbacks,
  setAdminSystemFeedback,
  setSystemFeedbacks,
} from "../actions/webinars";
import {
  ADMIN_CREATE_SYSTEM_FEEDBACKS,
  ADMIN_DELETE_SYSTEM_FEEDBACKS,
  ADMIN_GET_SYSTEM_FEEDBACKS,
  ADMIN_GET_SYSTEM_FEEDBACK_BY_ID,
  ADMIN_UPDATE_SYSTEM_FEEDBACKS,
} from "../constants/actions";
import {
  transformWebinarResponse,
  transformSingleWebinarResponse,
} from "../utils/webinar-response-transformer";

const {
  addWebinars,
  addRequestError,
  addUsersWebinars,
  setUsersWebinars,
  updateWebinar,
  loadVimeoVideoDataSaga,
  setTempWebinar,
  addErrorsSaga,
  addErrors,
  addValidateError,
  cleanErrors,
  addAdminWebinars,
  addAdminDashboardWebinars,
  startRequest,
  endRequest,
  setWebinars,
  addUserWebinarsAvailableYears,
  reloadDashboardWebinars,
  loadWebinarSubscription,
  saveSearchedWebinars,
  saveSearchedWebinarsTerms,
  loadWebinarsSaga: loadWebinarsAction,
  loadWebinarsComplete,
  cleanWebinars,
  loadClientWebinarsSaga,
} = actions;

const {
  API_DASHBOARDS_WEBINARS,
  LOAD_WEBINARS_SAGA,
  API_WEBINARS,
  LOAD_USERS_WEBINARS_SAGA,
  LOAD_WEBINAR_SAGA,
  CREATE_WEBINAR_SAGA,
  ERROR_TYPE_REQUEST,
  UPDATE_WEBINAR_SAGA,
  UPDATE_WEBINAR_SAGA_WITH_SPEAKER,
  DELETE_WEBINAR_SAGA,
  UPCOMING_WEBINARS,
  CURRENT_WEBINARS,
  PREVIOUS_WEBINARS,
  API_UPCOMING_WEBINARS,
  API_PREVIOUS_WEBINARS,
  API_LANDING_WEBINARS,
  API_CURRENT_WEBINARS,
  API_USERS_WEBINARS,
  API_ADMIN_WEBINARS,
  LOAD_ADMIN_WEBINARS_SAGA,
  LOAD_ADMIN_WEBINAR_SAGA,
  ADMIN_DELETE_WEBINARS_SAGA,
  ADMIN_DELETE_BOOKING_SAGA,
  LOAD_ADMIN_DASHBOARD_WEBINARS_SAGA,
  API_DASHBOARDS,
  API_LIVE_WEBINARS,
  API_RECORDED_WEBINARS,
  RELOAD_ALL_DASHBOARD_WEBINARS_SAGA,
  RECORDED_WEBINARS,
  LOAD_WEBINARS_AVAILABLE_YEARS_SAGA,
  API_LOAD_USER_WEBINARS_YEARS,
  LIVE_WEBINARS,
  SEARCH_WEBINARS,
  SEARCH_WEBINARS_TERMS,
  API_CERTIFICATES,
  GET_SYSTEM_FEEDBACKS,
  API_SYSTEM_FEEDBACKS,
  ADMIN_API_SYSTEM_FEEDBACKS,
  REF_TYPES,
} = constants;

const apiAdminGetFeedback = (id) =>
  axios.get(`${ADMIN_API_SYSTEM_FEEDBACKS}/${id}`);

export const apiGetCertificates = async (id) => {
  try {
    if (!id) {
      return;
    }

    const response = await axios.get(`${API_CERTIFICATES}/${id}`, {
      responseType: "blob",
    });

    jsFileDownload(response.data, "certificate.pdf");
  } catch (error) {
    console.log(error);
  }
};

function* lastLoadedPage(webinarType) {
  return yield select((state) => state.webinars[webinarType].page);
}

function* lastLoadedCPDpage(webinarType) {
  return yield select((state) => {
    return state.webinars.usersWebinars[webinarType].page;
  });
}

const apiLoadWebinars = async (webinarType, params) => {
  const response = await axios.get(eval(`API_${webinarType}`), {
    params: {
      ...params,
    },
  });
  return transformWebinarResponse(response);
};

const apiGetSystemFeedbacks = (params) =>
  axios.get(API_SYSTEM_FEEDBACKS, {
    params,
  });

const apiGetAdminSystemFeedbacks = (params) =>
  axios.get(ADMIN_API_SYSTEM_FEEDBACKS, {
    params,
  });

const apiCreateAdminSystemFeedbacks = (formData) =>
  axios.post(ADMIN_API_SYSTEM_FEEDBACKS, formData);

const apiUpdateAdminSystemFeedbacks = (id, formData) =>
  axios.put(`${ADMIN_API_SYSTEM_FEEDBACKS}/${id}`, formData);

const apiDeleteAdminSystemFeedbacks = (ids) =>
  axios.post(
    `${ADMIN_API_SYSTEM_FEEDBACKS}/destroy?${ids
      .map((id) => `feedback_ids[]=${id}`)
      .join("&")}`,
  );

const apiLoadWebinar = async (id, params = {}, fromQR = false) => {
  const response = fromQR
    ? await axios.get(`api/v1/join_webinar_qrcode/${id}`)
    : await axios.get(`${API_WEBINARS}/${id}`, {
        params: {
          ...params,
        },
      });
  return transformSingleWebinarResponse(response);
};

const apiLoadAdminWebinar = async (id, params = {}) => {
  const response = await axios.get(`${API_ADMIN_WEBINARS}/${id}`, {
    params: {
      ...params,
    },
  });
  // Admin endpoints may not use the new format yet, so check before transforming
  return transformSingleWebinarResponse(response);
};

const apiCreateWebinar = (webinar) => axios.post(API_ADMIN_WEBINARS, webinar);

const apiUpdateWebinar = (id, webinar) =>
  axios.put(`${API_ADMIN_WEBINARS}/${id}`, webinar);

const apiLoadUsersWebinars = async (webinarType, page, group_id, filters) => {
  const path = eval(`API_${webinarType}`);
  const response = await axios.get(path, {
    params: {
      page: page,
      per_page: 3,
      ...(group_id ? { group_id } : {}),
      ...(filters || {}),
    },
  });
  return transformWebinarResponse(response);
};

const apiLoadAdminWebinars = (page, query) =>
  axios.get(API_ADMIN_WEBINARS, {
    params: {
      page,
      per_page: 50,
      ...query,
    },
  });

const apiLoadAdminDashboardWebinars = (page, params) =>
  axios.get(API_DASHBOARDS_WEBINARS, {
    params: {
      page,
      per_page: 50,
      ...params,
    },
  });

const apiLoadUserWebinarYears = () =>
  axios.get(`${API_LOAD_USER_WEBINARS_YEARS}`);

const apiSearchWebinars = async ({ value, isTerm = false, isLogged }) => {
  const response = await axios.get(
    `api/v1/search_webinars${isTerm ? "/terms" : ""}?page=1&per_page=100&${
      isTerm ? "term_search" : "search"
    }=${value}${
      isLogged && !isTerm
        ? "&filter_by_county=true&filter_by_user_types=true&filter_by_wimin=true&filter_by_user_disciplines=true&filter_without_faculty=true"
        : ""
    }`,
  );
  return transformWebinarResponse(response);
};

const apiDeleteWebinars = (webinar_ids) => {
  return axios.post(
    API_ADMIN_WEBINARS +
      "/destroy" +
      `?${webinar_ids.map(
        (item, index) => `${index > 0 ? "&" : ""}webinar_ids[]=${item}`,
      )}`,
  );
};

const formatRequestData = (webinar) => {
  let data = new FormData();

  for (let i in webinar) {
    if (webinar[i] !== "" && webinar[i] !== null)
      data.append(`webinar[${i}]`, webinar[i]);
  }

  data.append("webinar[gms_study_days]", webinar.gms_study_days);

  if (webinar.document === null) {
    data.append("webinar[document]", webinar.document);
  }

  if (webinar.ppt_document === null) {
    data.append("webinar[ppt_document]", webinar.ppt_document);
  }

  data.delete("webinar[add_custom_text]");
  data.delete("webinar[galery_image_ids]");
  data.delete("webinar[galery_images]");
  data.delete("webinar[gallery_photo]");

  if (webinar.gallery_photo) {
    data.append("webinar[galery_image_ids][]", webinar.gallery_photo);
  } else if (webinar.gallery_photo === "")
    data.append("webinar[galery_image_ids][0]", "");

  const isCertificateOfParticipation =
    webinar.ref_type === REF_TYPES.CERTIFICATE_OF_PARTICIPATION;

  const isCustomCertificate = isCertificateOfParticipation;

  if (isCustomCertificate) {
    data.delete("webinar[cpd_points]");
    data.delete("webinar[int_cpd_points]");
    data.delete("webinar[ext_cpd_points]");
  }

  if (webinar.webinar_type != "online") {
    data.delete("webinar[zoom_uid]");
    data.delete("webinar[start_date]");
    data.delete("webinar[end_date]");
  }

  if (!webinar.add_custom_text) {
    data.set("webinar[custom_text_1]", "");
    data.set("webinar[custom_text_2]", "");
  }

  data.delete("webinar[counties]");
  data.delete("webinar[sponsor_ids]");
  data.delete("webinar[user_sponsor_ids]");
  data.delete("webinar[partnerships_ids]");
  data.delete("webinar[user_types]");
  data.delete("webinar[disciplines]");
  data.delete("webinar[assignedUsers]");
  data.delete("webinar[assigned_ids]");
  data.delete("webinar[attachmentPreview]");
  webinar.search_disciplines.forEach((id) => {
    data.append(`webinar[search_discipline_ids][]`, id);
  });
  webinar.counties.forEach((county) => {
    data.append(`webinar[counties][]`, county);
  });
  webinar.user_types.forEach((role) => {
    data.append(`webinar[user_types][]`, role);
  });
  webinar.disciplines.forEach((discipline) => {
    data.append(`webinar[disciplines][]`, discipline);
  });
  // if (webinar.assignedUsers) {
  //   webinar.assignedUsers.forEach((assignedUser, index) => {
  //     data.append(`webinar[assigned_ids][]`, parseInt(assignedUser));
  //   });
  // }
  if (webinar.assigned_ids && webinar.assigned_ids.length != 0) {
    webinar.assigned_ids.forEach((assigned_id) => {
      if (assigned_id != "NaN") {
        data.append(`webinar[assigned_ids][]`, parseInt(assigned_id));
      }
    });
  }
  if (webinar.assigned_ids && webinar.assigned_ids.length === 0) {
    data.append(`webinar[assigned_ids][]`, []);
  }

  if (webinar.sponsor_ids) {
    webinar.sponsor_ids.forEach((sponsor, index) => {
      data.append(`webinar[sponsor_ids][]`, parseInt(sponsor));
    });
  }
  if (webinar.partnerships_ids) {
    webinar.partnerships_ids.forEach((partner, index) => {
      data.append(`webinar[partnerships_ids][]`, parseInt(partner));
    });
  }
  if (webinar.user_sponsor_ids) {
    webinar.user_sponsor_ids.forEach((sponsorUser, index) => {
      data.append(`webinar[user_sponsor_ids][]`, parseInt(sponsorUser));
    });
  }

  data.delete("webinar[search_disciplines]");
  data.delete("webinar[spd_quizzes_attributes]");

  const quizzes = webinar.spd_quizzes_attributes.filter((e) => !!e.question);
  if (quizzes && quizzes.length)
    quizzes.forEach((quiz) => {
      if (quiz.id)
        data.append("webinar[spd_quizzes_attributes[]][id]", quiz.id);
      if (quiz._destroy)
        data.append(
          "webinar[spd_quizzes_attributes[]][_destroy]",
          quiz._destroy,
        );
      data.append("webinar[spd_quizzes_attributes[]][question]", quiz.question);
      data.append("webinar[spd_quizzes_attributes[]][correct]", quiz.correct);
    });

  data.delete("webinar[sessions_attributes]");

  const sessions = webinar.sessions_attributes.filter(
    (e) => !!e.rooms_attributes[0].title,
  );

  if (sessions && sessions.length)
    sessions.forEach((session) => {
      if (session.id)
        data.append("webinar[sessions_attributes[]][id]", session.id);
      if (session._destroy)
        data.append(
          "webinar[sessions_attributes[]][_destroy]",
          session._destroy,
        );
      data.append("webinar[sessions_attributes[]][title]", session.title);
      data.append(
        "webinar[sessions_attributes[]][start_time]",
        session.start_time,
      );
      data.append("webinar[sessions_attributes[]][end_time]", session.end_time);
      if (session.rooms_attributes && session.rooms_attributes.length)
        session.rooms_attributes.forEach((room) => {
          if (room.id)
            data.append(
              "webinar[sessions_attributes[]][rooms_attributes[]][id]",
              room.id,
            );
          if (room._destroy)
            data.append(
              "webinar[sessions_attributes[]][rooms_attributes[]][_destroy]",
              room._destroy,
            );
          data.append(
            "webinar[sessions_attributes[]][rooms_attributes[]][title]",
            room.title,
          );
        });
    });
  // const sessions = webinar.sessions_attributes.filter(e => !!e.rooms_attributes[0].title);
  // if (sessions && sessions.length)
  //   sessions.forEach(session => {
  //     data.append('webinar[sessions_attributes[]][title]', session.title);
  //     data.append('webinar[sessions_attributes[]][start_time]', session.start_time);
  //     data.append('webinar[sessions_attributes[]][end_time]', session.end_time);
  //     session.rooms_attributes.forEach(room => {
  //       data.append('webinar[sessions_attributes[]][rooms_attributes[]][title]', room.title);
  //     })
  //   })

  data.delete("webinar[experts_attributes]");

  const experts = webinar.experts_attributes.filter((e) => !!e.name);
  if (experts && experts.length)
    experts.forEach((expert) => {
      if (expert.id)
        data.append("[webinar][experts_attributes][][id]", expert.id);
      if (expert._destroy)
        data.append(
          "[webinar][experts_attributes][][_destroy]",
          expert._destroy,
        );
      data.append("[webinar][experts_attributes][][name]", expert.name);
      data.append("[webinar][experts_attributes][][location]", expert.location);
      data.append(
        "[webinar][experts_attributes][][expert_type]",
        expert.expert_type,
      );
      data.append(
        "[webinar][experts_attributes][][presentation_title]",
        expert.presentation_title || "",
      );
      if (expert.expert_avatar_url)
        data.append(
          "[webinar][experts_attributes][][avatar]",
          expert.expert_avatar_url,
        );
      if (expert.numeration)
        data.append(
          "[webinar][experts_attributes][][numeration]",
          expert.numeration,
        );
    });

  if (!webinar.vimeo_url || webinar.vimeo_url === " ")
    data.delete("webinar[vimeo_url]");

  return data;
};

const reloadWebinarsTimeout = (
  webinarType,
  page,
  params,
  paramsForRequest,
  newPage,
) => {
  return eventChannel((emmiter) => {
    const task = apiLoadWebinars(webinarType, paramsForRequest)
      .then((response) => {
        const webinars = response.data.webinars;
        const pages = response.data.pages;
        const totalCount = response.data.count;
        // if (!webinars.length) return;
        emmiter(addWebinars(webinarType, newPage, webinars, pages, totalCount));
        emmiter(loadWebinarsComplete());
        emmiter(END);
      })
      .catch((error) => {
        const { response } = error;
        console.log("WEBINARS ERROR -> ", response);

        emmiter(cleanWebinars());
        emmiter(loadWebinarsComplete());

        if (
          (response || {}).status === 500 ||
          (response || {}).status === 502 ||
          (response || {}).status === 504
        ) {
          toast.error(
            `MedCafe\'s server has encountered a temporary error and is currently unable to complete your request. Request resending automatically.`,
          );
          setTimeout(
            () => emmiter(loadWebinarsAction(webinarType, page, params)),
            10000,
          );
        }
        emmiter(END);
      });

    return () => {
      clearTimeout(task);
      return task;
    };
  });
};

const formatGroupWebinarRequestData = (webinar) => {
  let data = new FormData();

  for (let i in webinar) {
    if (webinar[i] !== "" && webinar[i] !== null)
      data.append(`webinar[${i}]`, webinar[i]);
  }

  data.append("webinar[gms_study_days]", webinar.gms_study_days);

  const isCertificateOfParticipation =
    webinar.ref_type === REF_TYPES.CERTIFICATE_OF_PARTICIPATION;

  const isCustomCertificate = isCertificateOfParticipation;

  data.append("webinar[gms_study_days]", webinar.gms_study_days);

  if (isCustomCertificate) {
    data.delete("webinar[cpd_points]");
    data.delete("webinar[int_cpd_points]");
    data.delete("webinar[ext_cpd_points]");
  }

  if (webinar.document === null) {
    data.append("webinar[document]", webinar.document);
  }

  if (webinar.ppt_document === null) {
    data.append("webinar[ppt_document]", webinar.ppt_document);
  }

  if (webinar.webinar_type != "online") {
    data.delete("webinar[zoom_uid]");
    data.delete("webinar[start_date]");
    data.delete("webinar[end_date]");
    data.delete("webinar[join_url]");
  }

  data.delete("webinar[sponsor_ids]");
  data.delete("webinar[user_sponsor_ids]");
  data.delete("webinar[assignedUsers]");
  data.delete("webinar[assigned_ids]");
  data.delete("webinar[attachmentPreview]");

  webinar.search_disciplines.forEach((id) => {
    data.append(`webinar[search_discipline_ids][]`, id);
  });
  // if (webinar.assignedUsers) {
  //   webinar.assignedUsers.forEach((assignedUser, index) => {
  //     data.append(`webinar[assigned_ids][]`, parseInt(assignedUser));
  //   });
  // }
  if (webinar.assigned_ids && webinar.assigned_ids.length != 0) {
    webinar.assigned_ids.forEach((assigned_id) => {
      if (assigned_id != "NaN") {
        data.append(`webinar[assigned_ids][]`, parseInt(assigned_id));
      }
    });
  }
  if (webinar.assigned_ids && webinar.assigned_ids.length === 0) {
    data.append(`webinar[assigned_ids][]`, []);
  }
  if (webinar.user_sponsor_ids) {
    webinar.user_sponsor_ids.forEach((sponsorUser, index) => {
      data.append(`webinar[user_sponsor_ids][]`, parseInt(sponsorUser));
    });
  }

  if (webinar.sponsor_ids) {
    webinar.sponsor_ids.forEach((sponsor, index) => {
      data.append(`webinar[sponsor_ids][]`, parseInt(sponsor));
    });
  }

  data.delete("webinar[search_disciplines]");
  data.delete("webinar[spd_quizzes_attributes]");

  const quizzes = webinar.spd_quizzes_attributes.filter((e) => !!e.question);
  if (quizzes && quizzes.length)
    quizzes.forEach((quiz) => {
      if (quiz.id)
        data.append("webinar[spd_quizzes_attributes[]][id]", quiz.id);
      if (quiz._destroy)
        data.append(
          "webinar[spd_quizzes_attributes[]][_destroy]",
          quiz._destroy,
        );
      data.append("webinar[spd_quizzes_attributes[]][question]", quiz.question);
      data.append("webinar[spd_quizzes_attributes[]][correct]", quiz.correct);
    });

  data.delete("webinar[experts_attributes]");

  const experts = webinar.experts_attributes.filter((e) => !!e.name);
  if (experts && experts.length)
    experts.forEach((expert) => {
      if (expert.id)
        data.append("[webinar][experts_attributes][][id]", expert.id);
      if (expert._destroy)
        data.append(
          "[webinar][experts_attributes][][_destroy]",
          expert._destroy,
        );
      data.append("[webinar][experts_attributes][][name]", expert.name);
      data.append("[webinar][experts_attributes][][location]", expert.location);
      data.append(
        "[webinar][experts_attributes][][expert_type]",
        expert.expert_type,
      );
      data.append(
        "[webinar][experts_attributes][][presentation_title]",
        expert.presentation_title || "",
      );
      if (expert.expert_avatar_url)
        data.append(
          "[webinar][experts_attributes][][avatar]",
          expert.expert_avatar_url,
        );
      if (expert.numeration)
        data.append(
          "[webinar][experts_attributes][][numeration]",
          expert.numeration,
        );
    });

  if (!webinar.vimeo_url || webinar.vimeo_url === " ")
    data.delete("webinar[vimeo_url]");

  data.delete("webinar[sessions]");
  data.delete("webinar[sessions_attributes]");

  const sessions = webinar.sessions_attributes.filter(
    (e) => !!e.rooms_attributes[0].title,
  );

  if (sessions && sessions.length)
    sessions.forEach((session) => {
      if (session.id)
        data.append("webinar[sessions_attributes[]][id]", session.id);
      if (session._destroy)
        data.append(
          "webinar[sessions_attributes[]][_destroy]",
          session._destroy,
        );
      data.append("webinar[sessions_attributes[]][title]", session.title);
      data.append(
        "webinar[sessions_attributes[]][start_time]",
        session.start_time,
      );
      data.append("webinar[sessions_attributes[]][end_time]", session.end_time);
      if (session.rooms_attributes && session.rooms_attributes.length)
        session.rooms_attributes.forEach((room) => {
          if (room.id)
            data.append(
              "webinar[sessions_attributes[]][rooms_attributes[]][id]",
              room.id,
            );
          if (room._destroy)
            data.append(
              "webinar[sessions_attributes[]][rooms_attributes[]][_destroy]",
              room._destroy,
            );
          data.append(
            "webinar[sessions_attributes[]][rooms_attributes[]][title]",
            room.title,
          );
        });
    });

  return data;
};

function* loadWebinarsSaga({ webinarType, page, params = {} }) {
  try {
    // Guard against a missing/NaN stored page (e.g. after reloadWebinars or an
    // actioncable setWebinars that didn't pass a page). Coercing to 0 keeps
    // newPage a valid integer so the API never receives `page=NaN`.
    const loadedPage = (yield call(lastLoadedPage, webinarType)) || 0;
    if (loadedPage >= page && loadedPage != 0) return;
    const newPage = loadedPage + 1;

    let per_page_n;
    if (webinarType === "PREVIOUS_WEBINARS") {
      per_page_n = 10;
    } else if (webinarType === "UPCOMING_WEBINARS") {
      per_page_n = 20;
    } else {
      per_page_n = 20;
    }

    // Map webinarType to scope value
    const scopeMap = {
      UPCOMING_WEBINARS: "upcoming_webinars",
      CURRENT_WEBINARS: "current_webinars",
      PREVIOUS_WEBINARS: "previous_webinars",
    };

    const paramsForRequest = {
      page: newPage,
      per_page: per_page_n,
      scope: scopeMap[webinarType],
      ...(params.group_id ? { group_id: params.group_id } : {}),
    };
    const channel = yield call(
      reloadWebinarsTimeout,
      webinarType,
      page,
      params,
      paramsForRequest,
      newPage,
    );

    while (true) {
      const actionEmmiter = yield take(channel);

      yield put(actionEmmiter);
    }
    // const response = yield call(apiLoadWebinars, webinarType, paramsForRequest);
    // const webinars = response.data.webinars;
    // const pages = response.data.pages;
    // const totalCount = response.data.count;
    // if (!webinars.length) return;
    // yield put(addWebinars(webinarType, newPage, webinars, pages, totalCount));
  } catch (e) {
    // Channel handles the error and emits loadWebinarsComplete
    const { response } = e || {};
    console.log(
      "ERRRORORORO -> ",
      e,
      (response || {}).status === 500 ||
        (response || {}).status === 502 ||
        (response || {}).status === 504,
    );
    if (
      (response || {}).status === 500 ||
      (response || {}).status === 502 ||
      (response || {}).status === 504
    ) {
      toast.error(
        `MedCafe\'s server has encountered a temporary error and is currently unable to complete your request. Please try again in a few minutes. Sorry for the inconvenience.`,
      );
      // const timeout = setTimeout(function* () {
      //   yield put(loadWebinarsAction(webinarType, page, params));
      // }, 6000);
      // clearTimeout(timeout);
    }
  }
}

function* reloadWebinars({ params = {} }) {
  try {
    const [upcoming, current, previous] = yield select((state) => [
      state.webinars[UPCOMING_WEBINARS],
      state.webinars[CURRENT_WEBINARS],
      state.webinars[PREVIOUS_WEBINARS],
    ]);

    const upcomingResp = yield call(apiLoadWebinars, UPCOMING_WEBINARS, {
      page: 1,
      per_page: 10,
      scope: "upcoming_webinars",
      ...(params.group_id ? { group_id: params.group_id } : {}),
    });

    const currentResp = yield call(apiLoadWebinars, CURRENT_WEBINARS, {
      page: 1,
      per_page: 3,
      scope: "current_webinars",
      ...(params.group_id ? { group_id: params.group_id } : {}),
    });

    const previousResp = yield call(apiLoadWebinars, PREVIOUS_WEBINARS, {
      page: 1,
      per_page: 10,
      scope: "previous_webinars",
      ...(params.group_id ? { group_id: params.group_id } : {}),
    });
    yield put(
      setWebinars(
        UPCOMING_WEBINARS,
        upcomingResp.data.webinars,
        upcomingResp.data.count,
        1,
        upcomingResp.data.pages,
      ),
    );
    yield put(
      setWebinars(
        CURRENT_WEBINARS,
        currentResp.data.webinars,
        currentResp.data.count,
        1,
        currentResp.data.pages,
      ),
    );
    yield put(
      setWebinars(
        PREVIOUS_WEBINARS,
        previousResp.data.webinars,
        previousResp.data.count,
        1,
        previousResp.data.pages,
      ),
    );
  } catch (e) {
    console.error(e);
    yield put(addRequestError(e.response));
  }
}

function* loadUsersWebinarsSaga({ page, webinarType, year, group_id }) {
  try {
    // Map webinarType to scope value for CPD webinars
    const scopeMap = {
      LIVE_WEBINARS: "cpd_live_webinars",
      RECORDED_WEBINARS: "cpd_previous_webinars",
    };

    let newPage;
    if (!page) {
      const loadedPage = yield call(lastLoadedCPDpage, webinarType);
      if (loadedPage >= page && loadedPage != 0) return;
      newPage = loadedPage + 1;
    } else {
      newPage = page;
    }

    // Build params with only allowed parameters
    const filters = {
      page: newPage,
      per_page: 3,
      scope: scopeMap[webinarType],
      ...(group_id ? { group_id } : {}),
      ...(year ? { year } : {}),
    };

    const response = yield call(
      apiLoadUsersWebinars,
      webinarType,
      newPage,
      group_id,
      filters,
    );
    let cpds = response.data.cpd_points || 0;
    const pages = response.data.pages;
    const webinars = response.data.webinars;
    const years = response.data.years;
    if (
      (webinarType === RECORDED_WEBINARS || webinarType === LIVE_WEBINARS) &&
      page === 1
    ) {
      yield put(
        setUsersWebinars(newPage, webinarType, webinars, years, pages, cpds),
      );
    } else {
      yield put(
        addUsersWebinars(newPage, webinarType, webinars, years, pages, cpds),
      );
    }
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* loadUsersRecordedWebinarsSaga({ page, webinarType, year, group_id }) {
  try {
    // Map webinarType to scope value for CPD webinars
    const scopeMap = {
      LIVE_WEBINARS: "cpd_live_webinars",
      RECORDED_WEBINARS: "cpd_previous_webinars",
    };

    let newPage;
    if (!page) {
      const loadedPage = yield call(lastLoadedCPDpage, webinarType);
      if (loadedPage >= page && loadedPage != 0) return;
      newPage = loadedPage + 1;
    } else {
      newPage = page;
    }

    // Build params with only allowed parameters
    const filters = {
      page: newPage,
      per_page: 3,
      scope: scopeMap[webinarType],
      ...(group_id ? { group_id } : {}),
      ...(year ? { year } : {}),
    };

    const response = yield call(
      apiLoadUsersWebinars,
      webinarType,
      newPage,
      group_id,
      filters,
    );
    let cpds = response.data.cpd_points || 0;
    const pages = response.data.pages;
    const webinars = response.data.webinars;
    const years = response.data.years;
    yield put(
      addUsersWebinars(newPage, webinarType, webinars, years, pages, cpds),
    );
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* loadWebinarSaga({
  id,
  temp = false,
  loadVideo = false,
  trackView = false,
  fromQR = false,
  group_id,
  source = "",
}) {
  try {
    const params = trackView ? { track_view: true } : {};
    const groupId = group_id ? { group_id } : {};
    const response = yield call(
      apiLoadWebinar,
      id,
      { ...params, ...groupId },
      fromQR,
    );
    const webinar = response.data.webinar;
    if (temp) {
      yield put(setTempWebinar(webinar));
      yield put(loadVimeoVideoDataSaga(webinar.vimeo_url));
    } else {
      // Only update the individual webinar if we're not reloading the entire dashboard
      // This prevents showing incomplete data temporarily
      if (!group_id && !source) {
        yield put(reloadDashboardWebinars(params));
      } else if (source === "clientUrl") {
        const slug = window.location.pathname.replace("/", "");
        yield put(loadClientWebinarsSaga("upcoming", slug, 1));
        yield put(loadClientWebinarsSaga("previous", slug, 1));
      }
    }
  } catch (e) {
    if (e.response.data.error_messages) {
      toast.error(e.response.data.error_messages);
      // yield put(push('/cpd/live'))
    }
    yield put(addErrors(ERROR_TYPE_REQUEST, e.response));
  }
}

function* loadAdminWebinarSaga({
  id,
  temp = false,
  loadVideo = false,
  trackView = false,
}) {
  try {
    const params = trackView ? { track_view: true } : {};
    const response = yield call(apiLoadAdminWebinar, id, params);
    const webinar = response.data.webinar;
    if (temp) {
      yield put(setTempWebinar(webinar));
      yield put(loadVimeoVideoDataSaga(webinar.vimeo_url));
    } else {
      yield put(updateWebinar(webinar));
    }
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* createWebinarSaga({ webinar }) {
  try {
    if (yield select((store) => store.requests.indexOf("webinar_create") != -1))
      return;

    yield put(startRequest("webinar_create"));

    const isGroupWebinar = Boolean(webinar.group_id);
    const webinarConstraints = isGroupWebinar ? "groupWebinar" : "webinar";
    const errors = validator.validate(webinar, webinarConstraints);

    if (!_.isEmpty(errors)) {
      yield put(addValidateError(CREATE_WEBINAR_SAGA, errors));
      yield put(endRequest("webinar_create"));
      return;
    } else {
      yield put(cleanErrors(CREATE_WEBINAR_SAGA));
    }

    console.log("webinar", webinar);

    if (
      webinar.ref_type !== REF_TYPES.CERTIFICATE_OF_PARTICIPATION &&
      webinar.ref_type !== REF_TYPES.NMBI &&
      webinar.spd_quizzes_attributes.filter((e) => !!e.question && !e._destroy)
        .length < 10 &&
      webinar.cpd_status != "not_applicable" &&
      webinar.cpd_status != "applied_for"
    ) {
      toast.error("CPD Quiz should contain >= 10 questions");
      yield put(endRequest("webinar_create"));
      return;
    }

    const webinarRequestData = isGroupWebinar
      ? yield call(formatGroupWebinarRequestData, webinar)
      : yield call(formatRequestData, webinar);
    const response = yield call(apiCreateWebinar, webinarRequestData);
    const { group_id } = response.data.webinar;

    toast.success("Webinar successfully created!");

    if (Boolean(group_id)) {
      yield put(push(`/groups/${group_id}/webinars`));
    } else {
      yield put(push("/webinars/"));
    }
    yield put(endRequest("webinar_create"));
  } catch (e) {
    console.log(e);
    yield put(endRequest("webinar_create"));
    yield put(addErrorsSaga(CREATE_WEBINAR_SAGA, e.response));
  }
}

function* updateWebinarSaga({ webinar }) {
  try {
    const isGroupWebinar = Boolean(webinar.group_id);
    const webinarConstraints = isGroupWebinar ? "groupWebinar" : "webinar";
    const errors = validator.validate(webinar, webinarConstraints);

    console.log("errors", { errors });

    if (errors) {
      yield put(addValidateError(UPDATE_WEBINAR_SAGA, errors));
      return;
    } else {
      yield put(cleanErrors(UPDATE_WEBINAR_SAGA));
    }

    if (
      webinar.ref_type !== REF_TYPES.CERTIFICATE_OF_PARTICIPATION &&
      webinar.ref_type !== REF_TYPES.NMBI &&
      webinar.spd_quizzes_attributes.filter((e) => !!e.question && !e._destroy)
        .length < 10 &&
      webinar.cpd_status != "not_applicable" &&
      webinar.cpd_status != "applied_for"
    ) {
      toast.error("CPD Quiz should contain >= 10 questions");
      return;
    }
    const webinarRequestData = isGroupWebinar
      ? yield call(formatGroupWebinarRequestData, webinar)
      : yield call(formatRequestData, webinar);

    const response = yield call(
      apiUpdateWebinar,
      webinar.id,
      webinarRequestData,
    );
    const { group_id } = response.data.webinar;

    toast.success("Webinar successfully updated!");

    if (Boolean(group_id)) {
      yield put(push(`/groups/${group_id}/webinars`));
    } else {
      yield put(push("/webinars/"));
    }
    yield put(endRequest("webinar_update"));
  } catch (e) {
    yield put(addErrorsSaga(UPDATE_WEBINAR_SAGA, e.response));
  }
}

function* updateWebinarRemoveSpeakerSaga({ webinar }) {
  try {
    const isGroupWebinar = Boolean(webinar.group_id);
    const webinarConstraints = isGroupWebinar ? "groupWebinar" : "webinar";

    const errors = validator.validate(webinar, webinarConstraints);

    if (errors) {
      yield put(addValidateError(UPDATE_WEBINAR_SAGA, errors));
      return;
    } else {
      yield put(cleanErrors(UPDATE_WEBINAR_SAGA));
    }

    if (
      webinar.spd_quizzes_attributes.filter((e) => !!e.question && !e._destroy)
        .length < 10 &&
      webinar.vimeo_url &&
      webinar.cpd_status != "not_applicable" &&
      webinar.cpd_status != "applied_for"
    ) {
      toast.error("CPD Quiz should contain >= 10 questions");
      return;
    }

    const webinarRequestData = yield call(formatRequestData, webinar);
    const response = yield call(
      apiUpdateWebinar,
      webinar.id,
      webinarRequestData,
    );
    const updatedWebinar = response.data.webinar;
    toast.success("Speaker was removed.");
    //yield put(push('/webinars/'))
    yield put(endRequest("webinar_update"));
  } catch (e) {
    yield put(addErrorsSaga(UPDATE_WEBINAR_SAGA, e.response));
  }
}

function* deleteWebinars({ webinar_ids }) {
  if (!webinar_ids.length) return;
  const webinarIds = webinar_ids;
  try {
    yield call(apiDeleteWebinars, webinarIds);
    toast.success("Webinars successfully deleted!");
    const query = yield select((state) => state.webinars.adminWebinars.query) ||
      {};
    const page = yield select((state) => state.webinars.adminWebinars.page) ||
      1;
    yield call(loadAdminWebinars, { page, query });
  } catch (e) {
    yield put(addErrorsSaga(ADMIN_DELETE_WEBINARS_SAGA, e.response));
  }
}

function* loadAdminWebinars({ page = 1, query = {} }) {
  try {
    const response = yield call(apiLoadAdminWebinars, page, query);
    const webinars = response.data.webinars;
    const count = response.data.count;
    const totalPages = response.data.total_pages;
    yield put(addAdminWebinars(webinars, count, page, totalPages, query));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* loadAdminAdminDashboardWebinars({ page, params }) {
  try {
    const response = yield call(apiLoadAdminDashboardWebinars, page, params);
    const webinars = response.data.webinars;
    const count = response.data.count;
    const totalPages = response.data.total_pages;
    yield put(addAdminDashboardWebinars(webinars, count, page, totalPages));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* loadWebinarsAvailableYears() {
  try {
    const response = yield call(apiLoadUserWebinarYears);
    const years = response.data.years;
    yield put(addUserWebinarsAvailableYears(years));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* searchWebinarsSaga({ payload: { searchString } }) {
  try {
    const isLogged = yield select((store) => !_.isEmpty(store.users.user));
    const response = yield call(apiSearchWebinars, {
      value: searchString,
      isLogged,
    });
    const webinars = response.data.webinars;
    yield put(saveSearchedWebinars(webinars));
  } catch (e) {
    console.log(e);
    yield put(addRequestError(e.response));
  }
}

function* searchWebinarsTermsSaga({ payload: { searchString } }) {
  try {
    const response = yield call(apiSearchWebinars, {
      value: searchString,
      isTerm: true,
    });
    const terms = response.data.terms;
    const special =
      (searchString.toLowerCase() === "igpnea" ||
        searchString.toLowerCase() === "ipna") &&
      "Irish General Practice Nurses Educational Assoc.";
    yield put(
      saveSearchedWebinarsTerms([...(special ? [special] : []), ...terms]),
    );
  } catch (e) {
    console.log(e);
    yield put(addRequestError(e.response));
  }
}

function* getSystemFeedbacks({ payload }) {
  try {
    const { data } = yield call(apiGetSystemFeedbacks, payload);
    yield put(setSystemFeedbacks(data));
  } catch (e) {
    console.log(e);
    yield put(addRequestError(e.response));
  }
}

function* getAdminSystemFeedbacksSaga({ payload }) {
  try {
    const { data } = yield call(apiGetAdminSystemFeedbacks, payload);
    yield put(setSystemFeedbacks(data));
  } catch (e) {
    console.log(e);
    yield put(addRequestError(e.response));
  }
}

function* createAdminSystemFeedback({ payload }) {
  try {
    const { body, user_name } = payload;
    const formData = new FormData();
    formData.append("[system_feedback][body]", body);
    formData.append("[system_feedback][user_name]", user_name);
    yield call(apiCreateAdminSystemFeedbacks, formData);
    toast.success("Feedback has been created!");
    yield put(push("/feedbacks"));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* updateAdminSystemFeedback({ payload }) {
  try {
    const { body, user_name, id } = payload;
    const formData = new FormData();
    formData.append("[system_feedback][body]", body);
    formData.append("[system_feedback][user_name]", user_name);
    yield call(apiUpdateAdminSystemFeedbacks, id, formData);
    toast.success("Feedback has been updated!");
    yield put(push("/feedbacks"));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* deleteAdminSystemFeedbacks({ payload }) {
  try {
    const { selected, ...params } = payload;
    yield call(apiDeleteAdminSystemFeedbacks, selected);
    toast.success("Feedbacks has been deleted!");
    yield call(getAdminSystemFeedbacksSaga, params);
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* getAdminSystemFeedbackById({ payload }) {
  try {
    const { data } = yield call(apiAdminGetFeedback, payload);
    const { feedback } = data;
    yield put(setAdminSystemFeedback(feedback));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* __loadWebinarSaga() {
  yield takeEvery(LOAD_WEBINAR_SAGA, loadWebinarSaga);
}

function* __loadAdminWebinarSaga() {
  yield takeEvery(LOAD_ADMIN_WEBINAR_SAGA, loadAdminWebinarSaga);
}

function* __loadWebinarsSaga() {
  yield takeEvery(LOAD_WEBINARS_SAGA, loadWebinarsSaga);
}

function* __loadUsersWebinarsSaga() {
  yield takeEvery(LOAD_USERS_WEBINARS_SAGA, loadUsersWebinarsSaga);
}

function* __createWebinarSaga() {
  yield takeEvery(CREATE_WEBINAR_SAGA, createWebinarSaga);
}
function* __updateWebinarSaga() {
  yield takeEvery(UPDATE_WEBINAR_SAGA, updateWebinarSaga);
}

function* __updateWebinarRemoveSpeakerSaga() {
  yield takeEvery(
    UPDATE_WEBINAR_SAGA_WITH_SPEAKER,
    updateWebinarRemoveSpeakerSaga,
  );
}

function* __deleteWebinarSaga() {
  yield takeEvery(ADMIN_DELETE_WEBINARS_SAGA, deleteWebinars);
}

function* __loadAdminWebinars() {
  yield takeEvery(LOAD_ADMIN_WEBINARS_SAGA, loadAdminWebinars);
}

function* __loadDashboardAdminWebinars() {
  yield takeEvery(LOAD_ADMIN_WEBINARS_SAGA, loadAdminWebinars);
}

function* __loadAdminAdminDashboardWebinarsSaga() {
  yield takeLatest(
    LOAD_ADMIN_DASHBOARD_WEBINARS_SAGA,
    loadAdminAdminDashboardWebinars,
  );
}

function* __reloadWebinars() {
  yield takeEvery(RELOAD_ALL_DASHBOARD_WEBINARS_SAGA, reloadWebinars);
}

function* __loadWebinarsAvailableYearsSaga() {
  yield takeEvery(
    LOAD_WEBINARS_AVAILABLE_YEARS_SAGA,
    loadWebinarsAvailableYears,
  );
}

function* __searchWebinarsSaga() {
  yield takeEvery(SEARCH_WEBINARS, searchWebinarsSaga);
}

function* __searchWebinarsTermsSaga() {
  yield takeEvery(SEARCH_WEBINARS_TERMS, searchWebinarsTermsSaga);
}

function* __getSystemFeedbacks() {
  yield takeEvery(GET_SYSTEM_FEEDBACKS, getSystemFeedbacks);
}

function* __getAdminSystemFeedbacks() {
  yield takeEvery(ADMIN_GET_SYSTEM_FEEDBACKS, getAdminSystemFeedbacksSaga);
}

function* __createAdminSystemFeedback() {
  yield takeEvery(ADMIN_CREATE_SYSTEM_FEEDBACKS, createAdminSystemFeedback);
}

function* __updateAdminSystemFeedback() {
  yield takeEvery(ADMIN_UPDATE_SYSTEM_FEEDBACKS, updateAdminSystemFeedback);
}

function* __deleteAdminSystemFeedbacks() {
  yield takeEvery(ADMIN_DELETE_SYSTEM_FEEDBACKS, deleteAdminSystemFeedbacks);
}

function* __getAdminSystemFeedbackById() {
  yield takeEvery(ADMIN_GET_SYSTEM_FEEDBACK_BY_ID, getAdminSystemFeedbackById);
}

export {
  __loadWebinarsSaga,
  __loadUsersWebinarsSaga,
  __loadWebinarSaga,
  __loadAdminWebinarSaga,
  __createWebinarSaga,
  __updateWebinarSaga,
  __updateWebinarRemoveSpeakerSaga,
  __deleteWebinarSaga,
  __loadAdminWebinars,
  __loadAdminAdminDashboardWebinarsSaga,
  __reloadWebinars,
  __loadWebinarsAvailableYearsSaga,
  __searchWebinarsSaga,
  __searchWebinarsTermsSaga,
  __getSystemFeedbacks,
  __getAdminSystemFeedbacks,
  __createAdminSystemFeedback,
  __getAdminSystemFeedbackById,
  __updateAdminSystemFeedback,
  __deleteAdminSystemFeedbacks,
};



// WEBPACK FOOTER //
// ./src/sagas/webinars.js